import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getApiErrorMessage } from '../api/client';
import { useCollapsibleGroups } from '../hooks/useCollapsibleGroups';
import { toast } from 'sonner';
import {
  maintenanceNoticeTitle, pickerMaintenanceWarnings, savedMaintenanceWarnings,
} from '../lib/maintenance-warnings';
import {
  AssignmentMaintenanceWarnings, MaintenanceNoticeBody,
} from '../components/equipment/AssignmentMaintenanceWarnings';
import {
  ArrowLeft,
  Loader,
  Search,
  CheckCircle2,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  available: { label: 'Available', className: 'bg-green-500/20 text-green-400' },
  reserved: { label: 'Reserved', className: 'bg-purple-500/20 text-purple-400' },
  in_use: { label: 'In Use', className: 'bg-blue-500/20 text-blue-400' },
  left_on_site: { label: 'Left on Site', className: 'bg-orange-500/20 text-orange-400' },
  maintenance: { label: 'Maintenance', className: 'bg-red-500/20 text-red-400' },
  retired: { label: 'Retired', className: 'bg-slate-500/20 text-slate-400' },
};

/** A status this screen has no entry for claims nothing — never "Available". */
const UNKNOWN_STATUS = { label: '—', className: 'bg-slate-500/20 text-slate-400' };

/**
 * Statuses PATCH /dispatch/jobs/:id/equipment refuses on an ADD
 * (`NOT_ADDABLE_STATUSES` in dispatch.routes.ts). The API refuses the WHOLE
 * batch with a 409 if any one of these is in `add`, so they stay visible —
 * dispatch must see every asset — but cannot be picked here. A unit already on
 * this job is not an add and stays toggleable.
 */
const NOT_ADDABLE = new Set(['left_on_site', 'maintenance', 'retired']);

/** The office's Transfer to Job action lives on the Equipment page's rows. */
const TRANSFER_TO_JOB_LIST = '/equipment?status=left_on_site';

/**
 * Ids the 409 `equipment_not_addable` body names in `blocked`. Anything else —
 * another status, a body without the array — yields none, and the toast still
 * carries the API's message.
 */
function blockedIdsOf(error: any): string[] {
  const body = error?.response?.data;
  if (body?.code !== 'equipment_not_addable' || !Array.isArray(body.blocked)) return [];
  return body.blocked.map((b: any) => b?.id).filter((id: unknown): id is string => typeof id === 'string');
}

const FILTER_TABS = ['All', 'Available', 'In Use'] as const;
type FilterTab = typeof FILTER_TABS[number];

/**
 * The type this asset is filed under in THIS screen.
 *
 * `item.type` is the free-text cache column. The Equipment page reads a
 * different key — `typeKeyOf` there prefers the joined catalog name — so in
 * principle the two screens could file one asset under two headings.
 *
 * IN PRACTICE THEY AGREE ON EVERY LIVE ASSET. Re-measured across all six
 * production tenants 2026-09-09: zero rows whose cached name disagrees with
 * their catalog type, out of 719. The 0334 triggers keep the cache derived
 * from the FK on every write, rename included, and 0335 backfilled the 60 rows
 * that predated them. api#623 proposed adding the catalog join to this
 * endpoint and was closed unfixed for exactly that reason — it would have
 * changed no heading on any tenant.
 *
 * So this is a shape difference, not a live defect, and the honest note is why
 * it stays: this endpoint (GET /dispatch/jobs/:id/available-equipment) selects
 * `eq.type` and does not join equipment_types, so the catalog name is not on
 * the wire and the preference cannot be expressed here even if it mattered.
 * The 15 assets carrying no type id at all — Dutch 7, Lone Star 8 — are the
 * ones where neither screen has a catalog name to prefer; both fall back to
 * the free text and agree by that route rather than by the join.
 *
 * If this ever needs revisiting, MEASURE FIRST. The claim that sent someone
 * after api#623 was an undated row count in the other screen's docblock that
 * had been repaired weeks earlier.
 */
const groupKeyOf = (item: { type?: string | null }): string => item.type || 'Other';

export default function EquipmentAssignmentScreen() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterTab>('All');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);
  // Units the last save was refused over. Cleared per unit when it is deselected.
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set());

  // Closed by default, same reason as the Equipment page: a dozen types and
  // 200+ assets, and the operator is picking a TYPE first. Unlike that page
  // this one HAS a search box, which is exactly why the two auto-expands below
  // are not optional — see them for what each one is holding up.
  const { isCollapsed, toggleGroup, expandGroups } = useCollapsibleGroups({ defaultCollapsed: true });

  const { data: equipment, isLoading } = useQuery({
    queryKey: ['available-equipment', jobId],
    queryFn: () => api.dispatch.getAvailableEquipment(jobId!).then(r => r.data.data ?? []),
    enabled: !!jobId,
  });

  // Initialize selectedIds from already_assigned once data loads
  if (equipment && !initialized) {
    const assigned = new Set<string>(
      equipment.filter((e: any) => e.already_assigned).map((e: any) => e.id as string)
    );
    setSelectedIds(assigned);
    // AUTO-EXPAND #1 — the groups that hold what is already on this job.
    //
    // Without it, collapsing by default means a dispatcher opens the picker for
    // a job and cannot see what is on the job. There is no selected-count
    // anywhere on this screen, so a checked box inside a shut group is not
    // merely inconvenient, it is unreadable — and unchecking is destructive:
    // `remove` hard-DELETEs the assignment row rather than hiding it.
    //
    // A seed, not a render-time force, so a group opened this way can still be
    // collapsed by hand afterwards.
    expandGroups(
      equipment.filter((e: any) => assigned.has(e.id)).map((e: any) => groupKeyOf(e))
    );
    setInitialized(true);
  }

  const filteredEquipment = useMemo(() => {
    if (!equipment) return [];
    let list = equipment;

    // Filter by status tab
    if (filter === 'Available') {
      list = list.filter((e: any) => e.status === 'available' || e.already_assigned);
    } else if (filter === 'In Use') {
      list = list.filter((e: any) => e.status === 'in_use' || e.already_assigned);
    }

    // Filter by search
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e: any) => {
        return (e.name || '').toLowerCase().includes(q) ||
          (e.manufacturer || '').toLowerCase().includes(q) ||
          (e.type || '').toLowerCase().includes(q) ||
          (e.serial_number || '').toLowerCase().includes(q);
      });
    }

    return list;
  }, [equipment, search, filter]);

  const groupedEquipment = useMemo(() => {
    const groups: { type: string; items: any[] }[] = [];
    const groupMap = new Map<string, any[]>();
    for (const item of filteredEquipment) {
      const type = groupKeyOf(item);
      if (!groupMap.has(type)) {
        const items: any[] = [];
        groupMap.set(type, items);
        groups.push({ type, items });
      }
      groupMap.get(type)!.push(item);
    }
    groups.sort((a, b) => a.type.localeCompare(b.type));
    return groups;
  }, [filteredEquipment]);

  /**
   * AUTO-EXPAND #2 — a search opens every group it returns.
   *
   * `groupedEquipment` is derived from `filteredEquipment`, so while a search
   * is running EVERY group on screen contains a match by construction. Without
   * this, typing a serial into the box on a collapsed list answers with a
   * column of shut headers reading `(1)` and the asset itself hidden behind a
   * click — the search reports a hit it does not show.
   *
   * A render-time force rather than a write, so clearing the search returns the
   * list to whatever the user had actually opened.
   */
  const searching = search.trim().length > 0;

  const toggleSelect = (id: string) => {
    const item = (equipment ?? []).find((e: any) => e.id === id);
    const turningOn = !selectedIds.has(id);
    if (!turningOn && blockedIds.has(id)) {
      setBlockedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // Picking an asset out of a SEARCH result opens its group for real, so that
    // clearing the search does not shut the selection back out of sight. The
    // search force above is temporary; this is the durable half of it.
    if (item && turningOn) expandGroups([groupKeyOf(item)]);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!equipment || !jobId) return;
      const originalAssigned = new Set<string>(
        equipment.filter((e: any) => e.already_assigned).map((e: any) => e.id as string)
      );
      const add = Array.from(selectedIds).filter(id => !originalAssigned.has(id));
      const remove = Array.from(originalAssigned).filter(id => !selectedIds.has(id));
      if (add.length === 0 && remove.length === 0) return;
      return api.dispatch.updateEquipment(jobId, { add, remove });
    },
    onSuccess: (res) => {
      // Maintenance warnings ride on a 200 and never change it: the save
      // worked. So a WARNING notice, never a success tick — a save that
      // carries advice must not read as a clean one. Absent (shadow/off) and
      // empty both show nothing; this screen shows no notice on a clean save.
      const maintenance = savedMaintenanceWarnings(res?.data);
      if (maintenance.length > 0) {
        toast.warning(maintenanceNoticeTitle(maintenance), {
          description: <MaintenanceNoticeBody warnings={maintenance} />,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-cert-summary'] });
      queryClient.invalidateQueries({ queryKey: ['home-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['field-my-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-report'] });
      queryClient.invalidateQueries({ queryKey: ['available-equipment', jobId] });
      navigate(`/jobs/${jobId}`, { state: { from: '/jobs' } });
    },
    // The API refuses the whole batch, so nothing was saved. Say so with the
    // API's own message (it names the unit and the remedy), leave the
    // selection exactly as the user built it, and mark the units it named.
    onError: (err: any) => {
      toast.error(getApiErrorMessage(err, 'Equipment was not saved. Nothing on this job changed.'));
      const ids = blockedIdsOf(err);
      setBlockedIds(new Set(ids));
      // Refresh statuses so the badges match what the server just refused on.
      // The selection is component state and survives the refetch.
      queryClient.invalidateQueries({ queryKey: ['available-equipment', jobId] });
      if (ids.length > 0) {
        expandGroups(
          (equipment ?? []).filter((e: any) => ids.includes(e.id)).map((e: any) => groupKeyOf(e))
        );
      }
    },
  });

  return (
    <div className="animate-fade-in min-h-full pb-[var(--bottom-chrome)] bg-fieldiq-dark flex flex-col">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <div>
            <h1 className="text-base font-bold text-foreground">Assign Equipment</h1>
            <p className="text-xs text-fieldiq-muted">Step 2 of 2</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fieldiq-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search equipment..."
            className="w-full bg-fieldiq-card border border-fieldiq-border rounded-lg pl-10 pr-8 py-2.5 text-sm text-white placeholder:text-fieldiq-muted focus:outline-none focus:ring-1 focus:ring-fieldiq-accent"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-fieldiq-muted">
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 mt-2">
          {FILTER_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                filter === tab
                  ? 'bg-fieldiq-accent text-fieldiq-dark'
                  : 'bg-fieldiq-card border border-fieldiq-border text-fieldiq-muted'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Equipment List */}
      <div className="flex-1 overflow-y-auto px-4 pb-36">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader size={28} className="animate-spin text-fieldiq-accent" />
          </div>
        ) : filteredEquipment.length === 0 ? (
          <div className="text-center py-12 px-4">
            {search || filter !== 'All' ? (
              <>
                <p className="text-fieldiq-muted text-sm">No equipment matches. Try clearing the filter.</p>
                <button
                  onClick={() => { setSearch(''); setFilter('All'); }}
                  className="mt-3 text-fieldiq-accent text-sm font-semibold hover:underline"
                >
                  Clear filters
                </button>
              </>
            ) : (
              <p className="text-fieldiq-muted text-sm">
                No available equipment. Check the Equipment Registry to make sure items are not all marked LEFT ON SITE or RESERVED.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {groupedEquipment.map(({ type, items }) => {
              const collapsed = !searching && isCollapsed(type);
              return (
                <div key={type}>
                  {/* Group header — sticky inside scroll container */}
                  <button
                    onClick={() => toggleGroup(type)}
                    aria-expanded={!collapsed}
                    className="sticky top-0 z-10 w-full flex items-center gap-2 px-2 py-2 bg-fieldiq-dark text-left"
                  >
                    {collapsed ? (
                      <ChevronRight className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                    )}
                    <span className="text-sm font-semibold text-white">{type}</span>
                    <span className="text-xs text-fieldiq-muted">({items.length})</span>
                  </button>

                  {/* Group items */}
                  {!collapsed && (
                    <div className="space-y-2 pb-2">
                      {items.map((item: any) => {
                        const isSelected = selectedIds.has(item.id);
                        const badge = STATUS_BADGE[item.status] || UNKNOWN_STATUS;
                        const isUnavailable = item.status === 'reserved' || item.status === 'in_use';
                        const showWarning = isSelected && isUnavailable && !item.already_assigned;
                        const notAddable = NOT_ADDABLE.has(item.status) && !item.already_assigned && !isSelected;
                        const isBlocked = blockedIds.has(item.id);

                        // The toggle is a button stretched over the card rather than the card
                        // itself, because the card holds a link to the equipment's current job
                        // and a link inside a button is invalid HTML — the browser flattens it
                        // and the tap toggles the row. Same shape on FieldJobEquipmentScreen.
                        const infoId = `equipment-row-${item.id}`;
                        return (
                          <div
                            key={item.id}
                            className={`relative w-full flex items-start gap-3 rounded-xl px-4 py-3 border transition-colors text-left ${
                              isBlocked
                                ? 'bg-red-500/10 border-red-500'
                                : isSelected
                                ? 'bg-fieldiq-accent/10 border-fieldiq-accent/40'
                                : 'bg-fieldiq-card border-fieldiq-border'
                            } ${notAddable ? 'opacity-70' : ''}`}
                          >
                            <button
                              type="button"
                              onClick={() => toggleSelect(item.id)}
                              disabled={notAddable}
                              aria-pressed={isSelected}
                              aria-labelledby={infoId}
                              className="absolute inset-0 w-full rounded-xl disabled:cursor-not-allowed"
                            />
                            {/* Checkbox */}
                            <div className="mt-0.5 flex-shrink-0">
                              {isSelected ? (
                                <CheckCircle2 className="w-5 h-5 text-fieldiq-accent" />
                              ) : (
                                <div className="w-5 h-5 rounded-full border-2 border-fieldiq-border" />
                              )}
                            </div>

                            {/* Info */}
                            <div id={infoId} className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                <span className="text-white text-sm font-semibold">{item.name}</span>
                                {(() => {
                                  const parts: string[] = [];
                                  if (item.manufacturer) parts.push(item.manufacturer);
                                  if (item.size) parts.push(item.size);
                                  if (item.serial_number) parts.push(`S/N: ${item.serial_number}`);
                                  return parts.length > 0 ? (
                                    <span className="text-white text-sm font-normal">· {parts.join(' · ')}</span>
                                  ) : null;
                                })()}
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  item.already_assigned ? 'bg-fieldiq-accent/20 text-fieldiq-accent' : badge.className
                                }`}>
                                  {item.already_assigned ? 'Assigned' : badge.label}
                                </span>
                              </div>

                              {/* Current job info */}
                              {item.current_job_number && !item.already_assigned && (
                                <p className={`${item.status === 'reserved' ? 'text-purple-400' : 'text-blue-400'} text-xs mt-1`}>
                                  {item.status === 'reserved' ? 'Reserved for' : 'Currently on'}{' '}
                                  {item.current_job_id ? (
                                    // Above the stretched toggle, and the click stops here so opening
                                    // the job does not also select the row.
                                    <Link
                                      to={`/jobs/${item.current_job_id}`}
                                      onClick={(e) => e.stopPropagation()}
                                      className="relative z-10 underline"
                                    >
                                      Job #{item.current_job_number}
                                    </Link>
                                  ) : (
                                    <>Job #{item.current_job_number}</>
                                  )}
                                </p>
                              )}

                              {/* Warning for selecting unavailable equipment */}
                              {showWarning && (
                                <div className="mt-1.5 flex items-start gap-1.5 text-amber-400 text-xs">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>
                                    {item.status === 'reserved' && `Reserved for${item.current_job_number ? ` Job #${item.current_job_number}` : ' another job'}`}
                                    {item.status === 'in_use' && `Currently in use${item.current_job_number ? ` on Job #${item.current_job_number}` : ''}`}
                                  </span>
                                </div>
                              )}

                              {/* Refused by the server while this list still read it as
                                  addable — the list was stale. The toast carries why. */}
                              {isBlocked && !NOT_ADDABLE.has(item.status) && (
                                <p className="mt-1.5 text-xs text-fieldiq-text font-semibold">
                                  Not saved — refused by the server. Deselect it to save the rest.
                                </p>
                              )}

                              {/* Not addable: say why in one line. A unit on location
                                  moves by Transfer to Job, not by an add. */}
                              {NOT_ADDABLE.has(item.status) && !item.already_assigned && (
                                <p className={`mt-1.5 text-xs ${isBlocked ? 'text-fieldiq-text font-semibold' : 'text-fieldiq-muted'}`}>
                                  {isBlocked && 'Not saved — '}
                                  {item.status === 'left_on_site' ? (
                                    <>
                                      On location; can't be added here. Use{' '}
                                      <Link
                                        to={TRANSFER_TO_JOB_LIST}
                                        onClick={(e) => e.stopPropagation()}
                                        className="relative z-10 underline"
                                      >
                                        Transfer to Job
                                      </Link>
                                      .
                                    </>
                                  ) : item.status === 'maintenance' ? (
                                    'In maintenance; can\'t be added to a job.'
                                  ) : (
                                    'Retired; can\'t be added to a job.'
                                  )}
                                  {isBlocked && isSelected && ' Deselect it to save the rest.'}
                                </p>
                              )}

                              {/* Maintenance watch, BEFORE selection, and never a
                                  gate: the row stays selectable and Save stays
                                  live. Nothing renders when the key is absent. */}
                              <AssignmentMaintenanceWarnings warnings={pickerMaintenanceWarnings(item)} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Action Bar — pb-44 on mobile lifts buttons clear of the bottom nav (md:hidden) and the AI FAB */}
      <div className="border-t border-fieldiq-border px-4 pt-3 pb-44 mt-4 sm:pb-3">
        <div className="max-w-[1920px] mx-auto flex gap-3">
          <button
            onClick={() => navigate(`/jobs/${jobId}`, { state: { from: '/jobs' } })}
            className="flex-1 py-3 rounded-lg border border-fieldiq-border text-fieldiq-text font-semibold text-sm hover:bg-fieldiq-border/30 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="flex-1 bg-fieldiq-accent text-fieldiq-dark font-bold py-3 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-amber-600 transition-colors"
          >
            {saveMutation.isPending ? (
              <><Loader size={18} className="animate-spin" /> Saving...</>
            ) : (
              'Save & Done'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
